import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Box, CircularProgress, Typography, Card, TextField, TablePagination } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ArgonBox from 'components/ArgonBox';
import ArgonSelect from 'components/ArgonSelect';
import { OrgContext } from 'context/orgContext';
import { useNotifications } from 'context/NotificationContext';
import { deployRCCustomObjects } from 'services/customObject';
import DeployExpandableTable from './DeployExpandableTable'; // Import the new table
import { ClearAll } from '@mui/icons-material';

function DeployView({ allData, sourceOrgId, onBack, targetOrg, setTargetOrg, selectedVersions, setSelectedVersions }) {
  const { orgs } = useContext(OrgContext);
  const { addNotification } = useNotifications();

  const [orgOptions, setOrgOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(6);

  useEffect(() => {
    if (orgs && orgs.length > 0) {
      const deployableOrgs = orgs.filter((org) => org.type === 'RC' && org.org_id !== sourceOrgId);
      setOrgOptions(deployableOrgs.map((org) => ({ label: org.name, value: org.org_id })));
    }
  }, [orgs, sourceOrgId]);

  const handleToggleVersion = (versionId) => {
    // Use the setter from props
    setSelectedVersions((prev) => ({
      ...prev,
      [versionId]: !prev[versionId],
    }));
  };

  const handleOrgChange = (event) => {
    setTargetOrg(event.target.value);
  };

  const handleDeploy = async () => {
    const versionIdsToDeploy = Object.keys(selectedVersions).filter((id) => selectedVersions[id]);

    if (!targetOrg) {
      addNotification({
        title: 'Validation Error',
        details: 'Please select a target organization.',
        severity: 'warning',
      });
      return;
    }
    if (versionIdsToDeploy.length === 0) {
      addNotification({
        title: 'Validation Error',
        details: 'Please select at least one version to deploy.',
        severity: 'warning',
      });
      return;
    }

    setIsLoading(true);
    addNotification({
      title: 'Deployment Started',
      details: `Deploying ${versionIdsToDeploy.length} item(s)...`,
      severity: 'info',
    });

    try {
      const response = await deployRCCustomObjects(targetOrg, versionIdsToDeploy);
      if (response.success) {
        const successes = response.result.filter((r) => r.status === 'fulfilled' && r.value.success).length;
        const failures = response.result.length - successes;
        addNotification({
          title: 'Deployment Complete',
          details: `Successfully deployed ${successes} item(s). ${failures > 0 ? `${failures} failed.` : ''}`,
          severity: failures > 0 ? 'warning' : 'success',
        });
      } else {
        throw new Error(response.error || 'Deployment failed at the API level.');
      }
    } catch (error) {
      addNotification({ title: 'Deployment Error', details: error.message, severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return allData;
    return allData.filter((group) => group.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [allData, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleClearSelections = () => {
    setSelectedVersions({});
    addNotification({ title: 'Selections Cleared', details: 'All versions have been deselected.', severity: 'info' });
  };

  const selectedCount = Object.values(selectedVersions).filter(Boolean).length;

  return (
    <Card>
      {/* HEADER */}
      <ArgonBox p={2} display="flex" justifyContent="flex-end" alignItems="center">
        <TextField
          placeholder="Search Objects..."
          variant="outlined"
          size="medium"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          sx={{ width: '300px', marginRight: 2 }}
        />

        <Button variant="contained" color="secondary" onClick={handleClearSelections} disabled={selectedCount === 0}>
          <ClearAll color="action" />
        </Button>
      </ArgonBox>

      {/* BODY (TABLE) */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress />
        </Box>
      ) : paginatedData.length > 0 ? (
        <ArgonBox p={2}>
          <DeployExpandableTable
            data={paginatedData}
            selectedVersions={selectedVersions}
            onToggleVersion={handleToggleVersion}
          />
        </ArgonBox>
      ) : (
        <Typography sx={{ textAlign: 'center', p: 3 }}>
          {searchTerm ? 'No objects match your search.' : 'No data available to deploy.'}
        </Typography>
      )}

      {/* FOOTER */}
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <Button variant="filled" color="info" startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back to List
        </Button>

        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          labelRowsPerPage=""
        />

        <Box display="flex" alignItems="center" gap={2}>
          <ArgonSelect
            placeholder="Select Target Org..."
            options={orgOptions}
            value={targetOrg || ''}
            onChange={handleOrgChange}
            sx={{ minWidth: '150px' }}
          />
          <Button
            variant="contained"
            color="info"
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <RocketLaunchIcon />}
            onClick={handleDeploy}
            disabled={isLoading || !targetOrg || selectedCount === 0}
          >
            {isLoading ? 'Deploying...' : `Deploy ${selectedCount} Item(s)`}
          </Button>
        </Box>
      </ArgonBox>
    </Card>
  );
}

DeployView.propTypes = {
  allData: PropTypes.array.isRequired,
  sourceOrgId: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default DeployView;
